import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING,
    STORAGE_AWS,
    STORAGE_GOOGLE,
    STORAGE_MICROSOFT,
} from "const"
import Images from "assets/images"
import { MtDriveFile, MtDriveVersion } from "server/models"
import {
    StoreType,
    UploadFile,
    getFile,
    createFileName,
    convertFileSize,
    addModal,
} from "client/store"
import { StorageType } from "client/utils"
import {
    FileIcon,
    DownloadIcon,
    Logo,
    DeleteButton,
    Empty,
    AmazonIcon,
    GoogleIcon,
    OneDriveIcon,
} from "client/components"


import Drive from "client/pages/drive"

import {
    sumHeight,
    Size,
    FileLoaderWrapper,
    FileLogoWrapper,
    FileLoaderBodyWrapper,
    FileDeleteWrapper,
    FileTitleWrapper,
    FileTitle,
    FileSize,
    FileInput,
    DownloadLink,
    FileButtonWrapper,
    FileButton,
} from "./styled"


interface DisplayFile {
    storageId: string
    storageSharedUrl: string
    storage: StorageType
    name: string
    size: number
}

const IMAGE = "image"
const FILE = "file"
const ANY = "any"

export interface FileLoaderProps {
    type?: typeof IMAGE | typeof FILE | typeof ANY
    forceStorage?: StorageType
    forceAccept?: string
    value: MtDriveFile | File
    autoLoad?: boolean
    showDownload?: boolean
    readOnly?: boolean
    onLoad?: (file: File) => void
    callback?: (file: MtDriveFile) => void
}

export const FileLoader = ({
    type = IMAGE,
    forceStorage = null,
    forceAccept = null,
    value = null,
    autoLoad = true,
    showDownload = false,
    readOnly = false,
    onLoad = null,
    callback = null,
}: FileLoaderProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const {
        imageType,
        fileType,
        defaultStorage,
    } = accountSettings.drive

    const [file, setFile] = useState<DisplayFile>(null)
    const [isLoaded, setIsLoaded] = useState(true)
    const targetStorage: StorageType = forceStorage || defaultStorage

    const createDisplayMtDriveFile = (driveFile: MtDriveFile) => {
        const name = createFileName(driveFile)
        setFile({
            storageId: driveFile.versions[0].storageId,
            storageSharedUrl: driveFile.versions[0].storageSharedUrl,
            storage: driveFile.storage,
            name,
            size: driveFile.versions[0].size,
        })
        setIsLoaded(true)
    }

    const createDisplayFile = (selectedFile: File) => {
        setFile({
            storageId: null,
            storageSharedUrl: null,
            storage: null,
            name: selectedFile.name,
            size: selectedFile.size,
        })
    }

    useEffect(() => {
        const valueMtDriveFile = value as MtDriveFile
        if (valueMtDriveFile && valueMtDriveFile.versions && valueMtDriveFile.versions.length) {
            createDisplayMtDriveFile(valueMtDriveFile)
        }

        const valueFile = value as File
        if (valueFile && valueFile.name && valueFile.size) {
            createDisplayFile(valueFile)
        }
    }, [value])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileInput = e.target
        const selectedFile = fileInput.files[0] || null
        fileInput.value = ""

        if (selectedFile) {
            // prevent uploading
            if (autoLoad === false) {
                createDisplayFile(selectedFile)
                if (onLoad) onLoad(selectedFile)
                return
            }

            setIsLoaded(false)

            UploadFile({
                file: selectedFile,
                storage: targetStorage,
                userData,
                projectId: project.id,
                targetFolderId: null,
                callbackSuccess: (newFile) => {
                    createDisplayMtDriveFile(newFile)
                    if (callback) callback(newFile)
                },
                callbackError: () => setIsLoaded(true),
            })
        }
    }

    const onDelete = () => {
        setFile({
            storageId: null,
            storageSharedUrl: null,
            storage: null,
            name: null,
            size: null,
        })
        if (callback) callback(null)
    }

    const openDriveModal = () => {
        dispatch(addModal({
            key: "drive",
            content: <Drive onSelectFile={(selectedFile) => {
                createDisplayMtDriveFile(selectedFile)
                if (callback) callback(selectedFile)
            }}
            />,
        }))
    }

    let accept = ""
    if (type === IMAGE) accept = imageType
    if (type === FILE) accept = fileType
    if (type === ANY) accept = `${imageType}${fileType}`
    accept = forceAccept || accept

    const emptyFile = type === IMAGE ? Images.noImage : null

    const srcLinks = file && file.storage && file.storageId
        ? getFile(file as unknown as MtDriveFile, file as unknown as MtDriveVersion)
        : null
    const title = file && file.name
        ? file.name
        : (readOnly ? <Empty /> : lang.selectFile)
    const size = file && file.size ? convertFileSize(file.size) : null

    const wrapperSize: Size = type !== IMAGE && !autoLoad ? "sm" : "lg"

    return (
        <FileLoaderWrapper size={wrapperSize} disabled={!isLoaded}>
            <FileLogoWrapper size={wrapperSize}>
                {
                    type === IMAGE
                    && <Logo logo={srcLinks ? srcLinks.preview : emptyFile} size={sumHeight} />
                }
                {
                    type !== IMAGE
                    && <FileIcon />
                }
            </FileLogoWrapper>
            <FileLoaderBodyWrapper size={wrapperSize}>
                <FileTitleWrapper>
                    {
                        !readOnly && (
                            <FileInput
                                type="file"
                                accept={accept}
                                title={lang.selectFile}
                                onChange={onChange}
                            />
                        )
                    }
                    <FileTitle>{title}</FileTitle>
                    {
                        size
                        && <FileSize>{size}</FileSize>
                    }
                </FileTitleWrapper>

                {
                    autoLoad && ( // hide buttons when file can not be uploaded
                        <FileButtonWrapper>
                            <FileButton
                                width={showDownload ? 30 : 50}
                                disabled={readOnly}
                                onClick={openDriveModal}
                                title={lang.selectFromDrive}
                            >
                                {lang.myDrive}
                            </FileButton>
                            <FileButton
                                width={showDownload ? 40 : 50}
                                disabled
                            >
                                { lang.storage }
                                { targetStorage === STORAGE_AWS && <AmazonIcon /> }
                                { targetStorage === STORAGE_GOOGLE && <GoogleIcon /> }
                                { targetStorage === STORAGE_MICROSOFT && <OneDriveIcon /> }
                            </FileButton>
                            {
                                showDownload && (
                                    <FileButton width={30}>
                                        <DownloadLink
                                            href={srcLinks ? srcLinks.download : emptyFile}
                                            className="download"
                                            target="_blank"
                                            title={lang.download}
                                            rel="noopener noreferrer"
                                            download={title}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <DownloadIcon />
                                        </DownloadLink>
                                    </FileButton>
                                )
                            }
                        </FileButtonWrapper>
                    )
                }
            </FileLoaderBodyWrapper>
            <FileDeleteWrapper disabled={readOnly}>
                <DeleteButton
                    status={isLoaded ? IDLE : PENDING}
                    onClick={onDelete}
                />
            </FileDeleteWrapper>
        </FileLoaderWrapper>
    )
}
